import { Injectable } from '@angular/core';
import { ConfigService } from 'providers/config.service';
import { AuthService } from 'providers/auth.service';
import { HttpClient } from '@angular/common/http';

@Injectable()
export class LogsApiService {
    constructor(
        private http: HttpClient,
        private authService: AuthService,
        private config: ConfigService
    ) {}

    /**
     * Fetches the latest, or searches for charging station log entries (e.g. system log messages)
     */
    async getStationLogs(
        stationId: string,
        queryParams?: GetStationLogsQueryParameters
    ): Promise<GetStationLogsResponse> {
        return this.http
            .get<GetStationLogsResponse>(
                `${this.config.restApiUrl}/chargingStations/${encodeURIComponent(stationId)}/logs?${Object.keys(
                    queryParams || {}
                )
                    .reduce(
                        (acc, k) =>
                            queryParams[k] !== undefined ? [...acc, `${k}=${encodeURIComponent(queryParams[k])}`] : acc,
                        []
                    )
                    .join('&')}`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Request station logs from station
     */
    async requestStationLogs(stationId: string, body: RequestStationLogsRequest): Promise<RequestStationLogsResponse> {
        return this.http
            .post<RequestStationLogsResponse>(
                `${this.config.restApiUrl}/chargingStations/${encodeURIComponent(stationId)}/logs/request`,
                body,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Downloads already fetched station logs as Excel
     */
    async downloadStationLogs(
        stationId: string,
        body: DownloadStationLogsRequest,
        queryParams?: DownloadStationLogsQueryParameters
    ): Promise<DownloadStationLogsResponse> {
        return this.http
            .post<DownloadStationLogsResponse>(
                `${this.config.restApiUrl}/chargingStations/${encodeURIComponent(stationId)}/logs/export?${Object.keys(
                    queryParams || {}
                )
                    .reduce(
                        (acc, k) =>
                            queryParams[k] !== undefined ? [...acc, `${k}=${encodeURIComponent(queryParams[k])}`] : acc,
                        []
                    )
                    .join('&')}`,
                body,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Fetches audit log entries by tenant
     */
    async getAuditLogs(queryParams?: GetAuditLogsQueryParameters): Promise<GetAuditLogsResponse> {
        return this.http
            .get<GetAuditLogsResponse>(
                `${this.config.restApiUrl}/auditlogs?${Object.keys(queryParams || {})
                    .reduce(
                        (acc, k) =>
                            queryParams[k] !== undefined ? [...acc, `${k}=${encodeURIComponent(queryParams[k])}`] : acc,
                        []
                    )
                    .join('&')}`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Fetches audit log entries by tenant
     */
    async getAuditLogsLocationUid(
        locationUid: string,
        queryParams?: GetAuditLogsQueryParameters
    ): Promise<GetAuditLogsResponse> {
        return this.http
            .get<GetAuditLogsResponse>(
                `${this.config.restApiUrl}/locations/${encodeURIComponent(locationUid)}/auditlogs?${Object.keys(
                    queryParams || {}
                )
                    .reduce(
                        (acc, k) =>
                            queryParams[k] !== undefined ? [...acc, `${k}=${encodeURIComponent(queryParams[k])}`] : acc,
                        []
                    )
                    .join('&')}`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Fetches audit log entries by tenant
     */
    async getAuditLogsStation(
        stationId: string,
        queryParams?: GetAuditLogsQueryParameters
    ): Promise<GetAuditLogsResponse> {
        return this.http
            .get<GetAuditLogsResponse>(
                `${this.config.restApiUrl}/chargingStations/${encodeURIComponent(stationId)}/auditlogs?${Object.keys(
                    queryParams || {}
                )
                    .reduce(
                        (acc, k) =>
                            queryParams[k] !== undefined ? [...acc, `${k}=${encodeURIComponent(queryParams[k])}`] : acc,
                        []
                    )
                    .join('&')}`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Fetches audit log entries by tenant
     */
    async getAuditLogsUser(userId: string, queryParams?: GetAuditLogsQueryParameters): Promise<GetAuditLogsResponse> {
        return this.http
            .get<GetAuditLogsResponse>(
                `${this.config.restApiUrl}/auditlogs/users/${encodeURIComponent(userId)}?${Object.keys(
                    queryParams || {}
                )
                    .reduce(
                        (acc, k) =>
                            queryParams[k] !== undefined ? [...acc, `${k}=${encodeURIComponent(queryParams[k])}`] : acc,
                        []
                    )
                    .join('&')}`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }
}

export interface DownloadStationLogsRequest {
    /**
     * elasticsearch filters like [{term: {unit: 'ok'}}]
     */
    filters: any[];
}

export interface DownloadStationLogsQueryParameters {
    type?: 'excel' | 'text';
}

export interface DownloadStationLogsResponse {
    url: string;
}

export interface GetAuditLogsResponse {
    logEntries: PickAuditLogRowBase[];
    lastEvaluatedKey: string;
}

export interface GetAuditLogsQueryParameters {
    /**
     * Number of log entries to return at most.
     * Defaults to 50, with maximum of 1000.
     */
    limit?: number;
    /**
     * Return logs starting after this key.
     * String provided will be converted at lambda.
     */
    exclusiveStartKey?: string;
}

export interface GetStationLogsQueryParameters {
    /**
     * Number of log entries to return at most.
     * Defaults to 10, with maximum of 1000.
     */
    limit?: number;
    /**
     * Return only logs entries after this date and time (ISO 8601 formatted).
     */
    startDate?: string;
    /**
     * Return only logs entries before this date and time (ISO 8601 formatted).
     */
    endDate?: string;
    /**
     * Search by this message
     */
    search?: string;
}

export interface GetStationLogsResponse {
    logEntries: LogEntry[];
}

export interface RequestStationLogsRequest {
    startDate?: string;
    endDate?: string;
}

export interface RequestStationLogsResponse {
    requestId: number;
    oldestTimestamp: string;
    latestTimestamp: string;
    response: GetLogResponse;
}

export interface LogEntry {
    /**
     * Date and time of the log entry (ISO 8601 format).
     */
    timestamp: string;
    /**
     * Message string
     */
    message: string;
    /**
     * Log message payload.
     */
    payload?: {
        [k: string]: any;
    };
    /**
     * Type of the log message (e.g. systemd-journal or log-stream), for decoding the "payload"
     */
    type: string;
    /**
     * log sequence number, if any - so that ordering of messages within the same millisecond remain correct
     */
    sequenceNo?: number;
}

export interface GetLogResponse {
    status: 'Accepted' | 'AcceptedCanceled' | 'Rejected';
    filename?: string;
    [k: string]: any;
}

export interface PickAuditLogRowBase {
    /**
     * Path to the lambda (e.g. /api/chargingStations/{stationId}/restartStation)
     */
    path?: string;
    /**
     * Source of invoke (e.g. "https://dev.kempower.io", "API request", "Internal trigger" )
     */
    source: string;
    /**
     * Various information about the user behind the action
     */
    user: {
        /**
         * ID of the user (e.g. 5cc833647c8d210fd27bf4ab)
         */
        userId: string;
        /**
         * Username
         */
        userName: string;
        /**
         * Email of user
         */
        userEmail: string;
        /**
         * IP address of the request source
         */
        userIP?: string;
        /**
         * IP address country code as ISO 3166-1 alpha-2.
         */
        userIPCountryCode?: string;
        /**
         * Issuer of authorization token (e.g. "https://login.dev.kempower.io/")
         */
        authTokenIss: string;
        /**
         * Authorization token creation time as ISO-8601
         */
        authTokenIat: string;
        /**
         * Authorization token audience (e.g. kdS5lBlfWNVJ37Uong5diPS9f4rp24tX)
         */
        authTokenAud: string;
        /**
         * Tenant ID
         */
        tenantId: string;
        /**
         * Tenant type (e.g. emsp)
         */
        tenantType: string;
    };
    /**
     * AuditLog ID
     */
    id: string;
    /**
     * API invoke timestamp as ISO-8601
     */
    timestamp: string;
    /**
     * Type of action performed (e.g. remote-start)
     */
    type: string;
    /**
     * ID of electric vehicle supply equipment
     */
    evseId?: string;
    /**
     * Location of charger
     */
    locationUid?: string;
    /**
     * Name of evse
     */
    evseName?: string;
    /**
     * ID of station configured
     */
    stationId?: string;
    /**
     * Related type and Id when available
     * (e.g. Transaction -> type: tx, id: transactionId)
     */
    related?: {
        type: string;
        id: string;
    };
    /**
     * Detail message about the action (e.g. Station restart requested, Immediate)
     */
    message: string;
    /**
     * Name of station configured
     */
    stationName?: string;
    /**
     * Name of location
     */
    locationName?: string;
}
